import React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Alert, Button, Container, FormGroup, Spinner,
} from 'reactstrap';
import cls from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faToolbox,
    faTruck,
    faCommentDots,
    faComments,
    faTimesCircle,
    faPhoneSquareAlt,
    faUser,
    faCircleNotch,
    faHardHat,
} from '@fortawesome/free-solid-svg-icons';
import { crewLeadLabel } from '@s20/i18n';
import { appendTimeZone } from '@s20/common/util/date';
import { getGroupSmsUrl, getMapUrl } from '@s20/common/util/dom';
import { getCounty } from '@s20/common/util/location';
import { httpClient } from '@s20/services/httpClient';
import {
    UncontrolledCollapseUnderlineHeader,
    FlexBetweenRow,
    FlexCenterRow,
    FlexColumnStart,
    FlexStartRow,
    FormLabel,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,

} from '../Common';
import {
    ContractTimesheetResponsibilityType,
    TimesheetResponsibilityType,
} from '../tenant/Tenant';
import authService from '../api-authorization/AuthorizeService';
import { JobDetails } from '../job/Job';
import { CountyLabel } from '../uscounties/CountyLabel';
import FileList from '../common/forms/FileList';
import './CancelConfirmation.scss';

export const ConfirmationStatus = {
    Unfilled: 1,
    Scheduled: 2,
    NoResponse: 3,
    Confirmed: 4,
    CancelPending: 5,
};

export const JobConfirmationStatusOptions = [
    { value: 1, class: 'unfilled', label: 'Unfilled' },
    { value: 2, class: 'scheduled', label: 'Scheduled' },
    { value: 3, class: 'noResponse', label: 'No Response' },
    { value: 4, class: 'confirmed', label: 'Confirmed' },
    { value: 5, class: 'cancelPending', label: 'Cancel Pending' },
];

class CancelConfirmation extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            assignment: null,
            confirmedAssignments: [],
            crewAndEquipOpen: true,
            invalidChargeTypeIds: [],
            jobDetailsOpen: false,
            loading: true,
            saving: false,
            notFound: false,
            timesheetOpen: true,
            timesheetValid: false,
            timesheetValidated: false,
            enforceCrewLeadTimesheet: true,
            usCounties: [],
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onAssignmentCheckedChanged = (id) => {
        const { confirmedAssignments } = { ...this.state };
        const inx = confirmedAssignments.indexOf(parseInt(id));
        if (inx < 0) confirmedAssignments.push(parseInt(id));
        else confirmedAssignments.splice(inx, 1);
        this.setState({ confirmedAssignments });
    };

    confirmCancelledAssignment = async () => {
        const { assignment } = { ...this.state };
        this.setState({ saving: true });
        const timesheetId = await httpClient.put(
            ApiRoutes.jobAssignmentCancellation.cancelConfirmed(
                assignment.jobAssignmentCancellationId,
            ),
        );

        this.setState({ saving: false });

        if (timesheetId !== 0) this.context.history.push(`${AppNavPaths.Timesheet}/${timesheetId}/${assignment.jobAssignmentId}`);
        else this.context.history.push(`${AppNavPaths.AssignmentCalendar}`);
    };

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const assignmentCancellationId = this.props.match.params.id;

            if (!assignmentCancellationId) {
                this.setState({ notFound: true, loading: false });
            } else {
                const response = await httpClient.get(
                    ApiRoutes.jobAssignmentCancellation.byId(
                        assignmentCancellationId,
                    ),
                    httpClient.format.none,
                );
                if (response.ok) {
                    const assignment = await response.json();

                    if (!assignment) {
                        this.setState({ notFound: true, loading: false });
                    } else {
                        /**
                         *  Determine who fills out the timesheets.  THis will be used to determine where to direct the user after
                         *  assignment confirmation.
                         * */
                        const usCounties = await httpClient.js(ApiRoutes.USCounties.all());
                        const enforceCrewLeadTS =
                            // Are we set for crew lead timesheets at the tenant level?
                            this.context.tenant.tenantSettings
                                .timesheetResponsibilityType
                            === TimesheetResponsibilityType.CrewLead
                            // Are we set for timesheets per contract, and contract is set up for crew lead?
                            || (this.context.tenant.tenantSettings
                                .timesheetResponsibilityType
                                === TimesheetResponsibilityType.Contract
                                && assignment.contractTimesheetResponsibilityTypeId
                                === ContractTimesheetResponsibilityType.CrewLead);

                        const confirmed = (assignment ?? {}).confirmationStatusId
                            === ConfirmationStatus.Confirmed;

                        this.setState({
                            assignment,
                            jobDetailsOpen: !confirmed,
                            loading: false,
                            enforceCrewLeadTimesheet: enforceCrewLeadTS,
                            usCounties,
                        });
                    }
                } else if (response.status === httpClient.statusCodes.unauthorized) {
                    this.context.history.push(
                        AppNavPaths.Unauthorized,
                    );
                } else {
                    toasty.error(
                        'There was a problem retrieving the job assignment.',
                    );
                }
            }
        }
    };

    submitTimesheet = async () => {
        this.setState({ timesheetValidated: false });
        await this.validateTimesheet();
        if (this.state.timesheetValid) {
            // TODO: save
        }
    };

    validateTimesheet = () => {
        const { assignment } = { ...this.state };
        const timeFormat = 'HH:mm a';
        const invalidChargeTypeIds = [];

        for (const a of assignment.timesheet.assignments) {
            const valid = !!a.notApplicable
                // Start
                || (!!a.startTime
                    && !!moment(a.startTime, timeFormat, true).isValid())
                // end
                || !!a.endTime
                || !!moment(a.endTime, timeFormat, true).isValid();
            if (!valid) invalidChargeTypeIds.push(a.id);
        }

        this.setState({
            timesheetValidated: true,
            timesheetValid:
                !invalidChargeTypeIds.length
                && !!assignment.timesheet.workOrderNumber,
            invalidChargeTypeIds,
        });
    };

    render() {
        const {
            assignment,
            confirmedAssignments,
            jobDetailsOpen,
            loading,
            saving,
            notFound,
            usCounties,
        } = { ...this.state };
        const { t } = this.props;
        const confirmed = (assignment ?? {}).confirmationStatusId
            === ConfirmationStatus.Confirmed;

        const crewLead = crewLeadLabel(this.context.tenant?.tenantSettings);

        if (loading || !(this.context.tenant ?? {}).tenantSettings) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <Spinner />
                </div>
            );
        }

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};

        const jobDetailsHeader = tenantSettings
            && tenantSettings.showContractDescriptionInTimesheetView
            && assignment
            && assignment.jobName
            ? t('confirm:jobDetailsWithName', { name: assignment.jobName })
            : t('confirm:jobDetails');

        const { jobUploadsEnabled } = {
            ...this.context.tenant.tenantSettings,
        };

        const allEquipment = assignment.equipment ?? [];

        // What equipment does the confirming employee own of this list?
        const ownedEquipment = allEquipment.filter(
            (x) => x.equipmentEmployeeAssignmentId
                === assignment.assignedEmployeeId,
        );
        const ownedEquipmentIds = ownedEquipment.map(
            (x) => x.assignedEquipmentId,
        );

        // Of the equipment, which have been assigned to any of the equipment owned by the confirming employee?
        const dependentEquipment = allEquipment.filter((x) => ownedEquipmentIds.includes(x.equipmentAssignedEquipmentId));
        const dependentEquipmentIds = dependentEquipment.map(
            (x) => x.assignedEquipmentId,
        );

        const jobStartTime = appendTimeZone(assignment?.jobStartTime ?? '', assignment?.jobStartTimeOffset, assignment?.timeZone);

        const county = getCounty(usCounties, {
            usCountyId: assignment?.usCountyId,
            contractUnionWorkTypeId: assignment?.contractUnionWorkTypeId,
        });

        return (
            <div className="confirmationContainer pt-2">
                <Container size="sm">
                    {!!notFound && (
                        <h4 className="text-center">
                            {t('confirm:notFound')}
                        </h4>
                    )}
                    {!!assignment && (
                        <>
                            {(this.state.assignment ?? {}).isConfirmed && (
                                <Alert
                                    className="mb-1 p-1 text-center site-alert site-alert-danger"
                                    color="danger"
                                >
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        size="lg"
                                        className="mr-2"
                                    />
                                    {t('confirm:alertCancelledAndConfirmed')}
                                </Alert>
                            )}

                            <UncontrolledCollapseUnderlineHeader
                                headerText={jobDetailsHeader}
                                isOpen // jobDetailsOpen}
                                toggleCollapse={() => this.setState({
                                    jobDetailsOpen: !jobDetailsOpen,
                                })}
                            >
                                {!(this.state.assignment ?? {})
                                    .isConfirmed && (
                                        <Alert
                                            className="mb-1 p-1 text-center site-alert site-alert-danger"
                                            color="danger"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                size="lg"
                                                className="mr-2"
                                            />
                                            {t('confirm:alertCancelled')}
                                        </Alert>
                                    )}
                                <JobDetails
                                    customerName={assignment?.customerName}
                                    subcontractorName={
                                        assignment?.subcontractorName
                                    }
                                    foremanName={assignment?.foremanName}
                                    foremanPhone={assignment?.foremanPhone}
                                    jobId={assignment?.jobId}
                                    projectId={assignment?.projectId}
                                    jobNotes={assignment?.jobNotes}
                                    jobNumber={assignment?.jobNumber}
                                    jobStartTime={jobStartTime}
                                    workOrderNumber={
                                        assignment?.workOrderNumber
                                    }
                                    tenantSettings={tenantSettings}
                                    jobName={assignment?.jobName}
                                />
                                <div className="locationDetails">
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="meetingLocation"
                                            label={t('confirm:meetingLocation')}
                                            className="bottom-border"
                                        />
                                        <FlexStartRow>
                                            {!!(assignment ?? {})
                                                .meetingLocationUrlFormatted && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title={t('timesheet:openInGoogleMaps')}
                                                        href={getMapUrl(assignment.meetingLocationUrlFormatted)}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                            <span
                                                id="meetingLocation"
                                                name="meetingLocation"
                                            >
                                                {assignment.meetingLocation
                                                    ?? '-'}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="jobLocation"
                                            label={t('confirm:jobLocation')}
                                            className="bottom-border"
                                        />
                                        <FlexStartRow>
                                            {!!(assignment ?? {})
                                                .jobLocationUrlFormatted && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title={t('timesheet:openInGoogleMaps')}
                                                        href={getMapUrl(assignment.jobLocationUrlFormatted)}
                                                        target="_blank"
                                                    >
                                                        <iName className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                            <span
                                                id="jobLocation"
                                                name="jobLocation"
                                            >
                                                {assignment.jobLocation
                                                    ?? '-'}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>

                                    {Boolean(assignment?.usCountyId) && (
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="usCounty"
                                                label={t('confirm:county')}
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                <div>
                                                    <CountyLabel {...county} />
                                                </div>
                                            </FlexStartRow>
                                        </FormGroup>
                                    )}

                                    {!!jobUploadsEnabled
                                        && !!(assignment.jobUploads ?? [])
                                            .length && (
                                            <div className="mt-3">
                                                <FormGroup>
                                                    <FormLabel
                                                        label={t('confirm:jobAttachments')}
                                                        className="bottom-border"
                                                    />
                                                    <FileList
                                                        jobId={assignment.jobId}
                                                        files={assignment.jobUploads ?? []}
                                                    />
                                                </FormGroup>

                                            </div>
                                        )}
                                </div>

                                {1 == 2 && ( //! !(this.state.assignment ?? {}).confirmationStatusId !== 5 &&
                                    <div className="crewAndEquipmentDetails">
                                        <FormGroup>
                                            <FormLabel
                                                label={t('confirm:crewAndEquipment')}
                                                className="bottom-border"
                                            />
                                        </FormGroup>
                                        {!!confirmed
                                            && !!assignment.crewSMS && (
                                                <FlexCenterRow>
                                                    <a
                                                        className="mb-2 p-1 btn btn-success btn-sm groupSmsLink text-white"
                                                        href={getGroupSmsUrl(assignment.crewSMS.split(','))}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faComments
                                                            }
                                                            size="lg"
                                                            className="mr-2"
                                                        />
                                                        {t('confirm:sendGroupSmsToCrew')}
                                                    </a>
                                                </FlexCenterRow>
                                            )}

                                        <div className="crewAndEquipmentDetails table-responsive">
                                            <table className="table table-sm assignmentsTable">
                                                <tbody>
                                                    {!!assignment.crew && (
                                                        <>
                                                            {assignment.crew.map(
                                                                (c) => {
                                                                    const ownedAssignment = c.assignedEmployeeId
                                                                        === assignment.assignedEmployeeId;

                                                                    return (
                                                                        <tr
                                                                            className={cls(
                                                                                'assignmentRow',
                                                                                {
                                                                                    owned: !!ownedAssignment,
                                                                                },
                                                                            )}
                                                                            key={
                                                                                c.id
                                                                            }
                                                                        >
                                                                            <td className="iconCell">
                                                                                <FontAwesomeIcon
                                                                                    className={cls(
                                                                                        {
                                                                                            'text-success font-weight-bold':
                                                                                                !!ownedAssignment,
                                                                                            'text-muted':
                                                                                                !ownedAssignment,
                                                                                            'fa-hard-hat-golden':
                                                                                                !!c.isCrewLead,
                                                                                        },
                                                                                    )}
                                                                                    icon={
                                                                                        c.isCrewLead
                                                                                            ? faHardHat
                                                                                            : faUser
                                                                                    }
                                                                                    title={
                                                                                        c.isCrewLead
                                                                                            ? crewLead
                                                                                            : c.assignedEmployeeGroup
                                                                                    }
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <FlexBetweenRow>
                                                                                    <small
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    c.assignedEmployeeId
                                                                                                    === assignment.assignedEmployeeId,
                                                                                            },
                                                                                        )}
                                                                                    >
                                                                                        {c.isCrewLead
                                                                                            ? t('confirm:assignedEmployeeWithLead', { name: c.assignedEmployeeName, lead: crewLead })
                                                                                            : c.assignedEmployeeName}
                                                                                    </small>
                                                                                    {!!confirmed
                                                                                        && !!c.assignedEmployeePhone && (
                                                                                            <a
                                                                                                title={`Send SMS to ${c.assignedEmployeeName}`}
                                                                                                className="ml-2 p-1 btn btn-sm btn-success"
                                                                                                href={`sms://+1${c.assignedEmployeePhone}`}
                                                                                            >
                                                                                                <FontAwesomeIcon
                                                                                                    icon={
                                                                                                        faCommentDots
                                                                                                    }
                                                                                                    size="lg"
                                                                                                />
                                                                                            </a>
                                                                                        )}
                                                                                </FlexBetweenRow>
                                                                            </td>
                                                                            <td className="text-right">
                                                                                {!confirmed
                                                                                    && c.confirmationStatusId
                                                                                    === ConfirmationStatus.Confirmed && (
                                                                                        <FontAwesomeIcon
                                                                                            style={{
                                                                                                opacity:
                                                                                                    ownedAssignment
                                                                                                        ? 1
                                                                                                        : 0.5,
                                                                                            }}
                                                                                            size="lg"
                                                                                            title={t('timesheet:employeeConfirmed')}
                                                                                            className="icon-check checked"
                                                                                            icon={
                                                                                                faCheckCircle
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                },
                                                            )}
                                                        </>
                                                    )}
                                                    {!!assignment.equipment && (
                                                        <>
                                                            {assignment.equipment.map(
                                                                (e) => {
                                                                    const icon = e.assignedEquipmentIsVehicle ? faTruck : faToolbox;

                                                                    const ownedAssignment = e.equipmentEmployeeAssignmentId
                                                                        === assignment.assignedEmployeeId
                                                                        || dependentEquipmentIds.includes(
                                                                            e.assignedEquipmentId,
                                                                        );

                                                                    return (
                                                                        <tr
                                                                            className={cls(
                                                                                'assignmentRow',
                                                                                {
                                                                                    owned: !!ownedAssignment,
                                                                                },
                                                                            )}
                                                                            key={
                                                                                e.id
                                                                            }
                                                                        >
                                                                            <td className="iconCell">
                                                                                <FontAwesomeIcon
                                                                                    className={cls(
                                                                                        {
                                                                                            'text-success font-weight-bold':
                                                                                                !!ownedAssignment,
                                                                                            'text-muted':
                                                                                                !ownedAssignment,
                                                                                        },
                                                                                    )}
                                                                                    icon={
                                                                                        icon
                                                                                    }
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <FlexColumnStart>
                                                                                    <small className={cls({ 'text-success font-weight-bold': !!ownedAssignment })}>
                                                                                        {t('confirm:assignedEquipmentBase', { type: e.assignedEquipmentType, name: e.assignedEquipmentName })}
                                                                                        {e.quantity && e.quantity > 1 ? ` ${t('confirm:quantitySuffix', { quantity: e.quantity })}` : ''}
                                                                                        {parseInt(assignment.dispatchOfficeId) !== e.equipmentDispatchLocation.id
                                                                                            ? ` ${t('confirm:dispatchedFrom', { name: e.equipmentDispatchLocation.name })}`
                                                                                            : ''}
                                                                                    </small>

                                                                                    {e.equipmentEmployeeAssignmentName && (
                                                                                        <small>
                                                                                            {t('confirm:assignedTo', { name: e.equipmentEmployeeAssignmentName })}
                                                                                        </small>
                                                                                    )}

                                                                                    {e.equipmentAssignedEquipmentName && (
                                                                                        <small>
                                                                                            {t('confirm:assignedToEquipment', { type: e.equipmentAssignedEquipmentType, name: e.equipmentAssignedEquipmentName })}
                                                                                        </small>
                                                                                    )}

                                                                                    {(e.rentalDropoffQuantity > 0 || e.rentalPickupQuantity > 0) && (
                                                                                        <>
                                                                                            {e.rentalDropoffQuantity > 0 && (
                                                                                                <div>
                                                                                                    <small>
                                                                                                        {t('confirm:dropoffWithQty', { quantity: e.rentalDropoffQuantity })}
                                                                                                    </small>
                                                                                                </div>
                                                                                            )}

                                                                                            {e.rentalPickupQuantity > 0 && (
                                                                                                <div>
                                                                                                    <small>
                                                                                                        {t('confirm:pickupWithQty', { quantity: e.rentalPickupQuantity })}
                                                                                                    </small>
                                                                                                </div>
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                                </FlexColumnStart>
                                                                            </td>
                                                                            <td className="text-right">
                                                                                {!confirmed
                                                                                    && e.confirmationStatusId
                                                                                    === ConfirmationStatus.Confirmed && (
                                                                                        <FontAwesomeIcon
                                                                                            style={{
                                                                                                opacity:
                                                                                                    ownedAssignment
                                                                                                        ? 1
                                                                                                        : 0.7,
                                                                                            }}
                                                                                            size="lg"
                                                                                            title={t('timesheet:equipmentConfirmed')}
                                                                                            className="icon-check checked"
                                                                                            icon={
                                                                                                faCheckCircle
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                {!confirmed
                                                                                    && e.confirmationStatusId
                                                                                    !== ConfirmationStatus.Confirmed
                                                                                    && ownedAssignment && (
                                                                                        <FontAwesomeIcon
                                                                                            style={{
                                                                                                opacity:
                                                                                                    ownedAssignment
                                                                                                        ? 1
                                                                                                        : 0.7,
                                                                                            }}
                                                                                            size="lg"
                                                                                            className={cls(
                                                                                                'cursor-pointer icon-check',
                                                                                                {
                                                                                                    checked:
                                                                                                        confirmedAssignments.indexOf(
                                                                                                            e.id,
                                                                                                        )
                                                                                                        > -1,
                                                                                                },
                                                                                            )}
                                                                                            title={
                                                                                                confirmedAssignments.indexOf(
                                                                                                    e.id,
                                                                                                )
                                                                                                    > -1
                                                                                                    ? 'Confirmed'
                                                                                                    : 'Click to confirm'
                                                                                            }
                                                                                            icon={
                                                                                                faCheckCircle
                                                                                            }
                                                                                            onClick={() => {
                                                                                                this.onAssignmentCheckedChanged(
                                                                                                    e.id,
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                },
                                                            )}
                                                        </>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </UncontrolledCollapseUnderlineHeader>
                        </>
                    )}
                </Container>

                <FlexCenterRow className="userActions p-1">
                    <Button
                        type="button"
                        id="officeCallButton"
                        href={`tel:${this.state?.assignment?.dispatchOfficeNumber}`}
                    >
                        <FontAwesomeIcon
                            size="lg"
                            icon={faPhoneSquareAlt}
                            className="mr-2"
                        />
                        {t('confirm:officeWithNumber', { number: this.state?.assignment?.dispatchOfficeNumber })}
                    </Button>
                    {!(this.state.assignment ?? {}).isConfirmed && (
                        <Button
                            type="button"
                            color="danger"
                            disabled={saving}
                            onClick={() => this.confirmCancelledAssignment()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={saving ? faCircleNotch : faCheckCircle}
                                className="mr-2"
                            />
                            {t('confirm:confirmCancellation')}
                        </Button>
                    )}
                </FlexCenterRow>
            </div>
        );
    }
}

export default withTranslation()(CancelConfirmation);
